/**
 * PaperJournalFilters — REW-929 Phase 3.
 *
 * Three filter inputs that narrow the history table (uploaded_by, from_date,
 * to_date) plus a separate Summary Window selector. The summary window is
 * filter-independent on the backend (window_only scope) and applies
 * immediately; table filters are applied via the Search button.
 */

import React, { useState } from 'react';
import { Input, DateInput, Select } from '@/components/common/FormField';
import { Button } from '@/components/common/Modal';
import type {
  PaperJournalListUploadsParams,
  PaperJournalSummaryWindow,
} from '@/app/types/paperJournal';

import './PaperJournalFilters.scss';

interface PaperJournalFiltersProps {
  initial: PaperJournalListUploadsParams;
  onApply: (next: Partial<PaperJournalListUploadsParams>) => void;
  onClear: () => void;
  onWindowChange: (window: PaperJournalSummaryWindow | undefined) => void;
}

const SUMMARY_WINDOW_OPTIONS = [
  { value: '', label: 'No summary' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export const PaperJournalFilters: React.FC<PaperJournalFiltersProps> = ({
  initial,
  onApply,
  onClear,
  onWindowChange,
}) => {
  const [uploadedBy, setUploadedBy] = useState<string>(initial.uploaded_by ?? '');
  const [fromDate, setFromDate] = useState<string>(initial.from_date ?? '');
  const [toDate, setToDate] = useState<string>(initial.to_date ?? '');
  const [summaryWindow, setSummaryWindowState] = useState<string>(
    initial.summary_window ?? '',
  );

  const handleSearch = () => {
    const next: Partial<PaperJournalListUploadsParams> = {};
    const trimmed = uploadedBy.trim();
    if (trimmed.length > 0) {
      next.uploaded_by = trimmed;
    }
    if (fromDate.length > 0) {
      next.from_date = fromDate;
    }
    if (toDate.length > 0) {
      next.to_date = toDate;
    }
    onApply(next);
  };

  const handleClear = () => {
    setUploadedBy('');
    setFromDate('');
    setToDate('');
    onClear();
  };

  const handleWindowChange = (value: string) => {
    setSummaryWindowState(value);
    const window =
      value === '' ? undefined : (value as PaperJournalSummaryWindow);
    onWindowChange(window);
  };

  return (
    <div className="paper-journal-filters">
      <div className="paper-journal-filters__row">
        <Input
          id="uploaded-by"
          label="Uploaded by (admin staff)"
          value={uploadedBy}
          onChange={setUploadedBy}
          placeholder="Admin email — not ref user id"
        />
        <DateInput
          id="from-date"
          label="From date"
          value={fromDate}
          onChange={setFromDate}
        />
        <DateInput
          id="to-date"
          label="To date"
          value={toDate}
          onChange={setToDate}
        />
        <Select
          id="summary-window"
          label="Summary window"
          value={summaryWindow}
          onChange={handleWindowChange}
          options={SUMMARY_WINDOW_OPTIONS}
        />
      </div>
      <div className="paper-journal-filters__actions">
        <Button onClick={handleSearch}>Search</Button>
        <Button variant="secondary" onClick={handleClear}>
          Clear
        </Button>
      </div>
    </div>
  );
};

export default PaperJournalFilters;
